import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import {
  getCourseByIdForUsers,
  getEnrolmentCoursesForUsers,
  getEnrolmentCoursesForUsersForApp,
  getEnrolmentCoursesForUsersForBundlePayment,
  getEnrolmentCoursesForUsersForNotification,
  getNonEnrolmentCoursesForUsers,
  loadMoreEnrolmentCoursesForUsers,
  loadMoreNonEnrolmentCoursesForUsers,
  sendCourseCreditReminderNotification,
  updateCourse,
} from './api';
import { RootState } from '../..';
import {
  createCourseAsAdmin,
  createCourseByTemplateAsAdmin,
  deleteCourseAsAdmin,
  getCourseByIdForAdmin,
  getCourseListForDropDownAsAdmin,
  getCoursesForAdmin,
  loadMoreCoursesForAdmin,
  updateCourseAsAdmin,
} from './adminApi';
import { ISection } from '../SectionSlice';
import { updateCourseAsAdminSavingSlice } from '../SavingSlice';
import {
  ICourseTutor,
  createCourseTutor,
  deleteCourseTutor,
  updateCourseTutor,
} from '../CourseTutorsSlice';
import { TIdentifiedUserRole } from '../../UserSlice';
import { ICoursePricingTemplate } from '../CoursePricingTemplatesSlice';
import { IGroupTutorRateCourseType } from '../GroupTutorRateCourseTypesSlice';
import { TSubscriptionPlanBillingPeriodTypes } from '../SubscriptionPlansSlice';
import { ICoursePermissionTemplate } from '../CoursePermissionTemplatesSlice';
import { ICourseStudent } from '../CourseStudentsSlice';
import {
  updateCourseStudent,
  updateCourseStudentLearningDetails,
} from '../CourseStudentsSlice/api';

export type TCourseStatus = 'ACTIVE' | 'DRAFT';
export type TCourseEnrolment = 'ENROLLED' | 'UNENROLLED';

export const COURSE_STATUS_OPTIONS = {
  ACTIVE: 'ACTIVE',
  DRAFT: 'DRAFT',
};

export const COURSE_ENROLMENT_OPTIONS = {
  ENROLLED: 'ENROLLED' as TCourseEnrolment,
  UNENROLLED: 'UNENROLLED' as TCourseEnrolment,
};

export const COURSE_PRICING_TYPES = {
  ONETIME: 'ONETIME',
  SUBSCRIPTION: 'SUBSCRIPTION',
  CREDIT: 'CREDIT',
} as const;

export const COURSE_MODES = {
  ONE_TO_ONE: 'One-to-One',
  GROUP: 'Group',
};

export const COURSE_TYPES = {
  COURSE: 'COURSE',
  CLASS: 'CLASS',
} as const;

export const defaultPricingDetails = [
  'Structured Syllabus Completion',
  'Trusted, Certified Subject Tutors',
  'Live, Interactive 1-on-1 Lessons',
  'Exam-Focused Teaching Approach',
  'Past Paper Practice for Real Exam Prep',
  'Recorded Lessons to Revisit Anytime',
  'Progress Tracking & Regular Feedback',
  "Exclusive, discounted access to Tutopiya's premium learning resources",
  'Flexibility to transfer unused credits across different courses',
];

export type TCoursePricingType = typeof COURSE_PRICING_TYPES[keyof typeof COURSE_PRICING_TYPES];
export type TCourseMode = typeof COURSE_MODES[keyof typeof COURSE_MODES];
export type TCourseType = typeof COURSE_TYPES[keyof typeof COURSE_TYPES];

export interface ICoursePricing {
  type: TCoursePricingType;
  amount?: number;
  currency?: string;
  billingPeriod?: {
    count: number;
    type: TSubscriptionPlanBillingPeriodTypes;
  };
}

export interface ICourse {
  _id: string;
  title: string;
  subTitle?: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  status: TCourseStatus;
  sections: Array<ISection>;
  type?: TCourseType;
  pricings?: ICoursePricing[];
  curriculum?: string;
  grade?: string;
  subject?: string;
  noOfClasses?: number;
  duration?: number;
  startDate?: string;
  endDate?: string;
  classTime?: string;
  classDays?: string[];
  pricingTemplate?: string | ICoursePricingTemplate;
  permissionTemplate?: string | ICoursePermissionTemplate;
  pricingDetails?: Array<string>;
  tutors?: Array<ICourseTutor>;
  students?: Array<ICourseStudent>;
  identifiedCourseUserRole?: TIdentifiedUserRole;
  groupClassId?: string;
  isOneToOne?: boolean;
  visibility?: boolean;
  selfEnrolement?: boolean;
  template?: string;
  isSubscribed?: boolean;
  isLessonBookingEnabled?: boolean;
  templateSteps?: {
    isCompleted?: boolean;
    step1?: 'completed';
    step2?: 'completed';
    step3?: 'completed';
    step4?: 'completed';
  };
  groupTutorRateCourseType?: string | IGroupTutorRateCourseType;
  chat?: {
    isChatEnabled: boolean;
    isEveryoneCanChat: boolean;
  };
  isTrial?: boolean;
  rate?: {
    amount: number;
    currency: string;
  };
  lastCoursePurchasedRate?: {
    amount: number;
    currency: string;
  };
  stripeProductId?: string;
  trialMissed?: boolean;
  trialCancelled?: boolean;
  hasActiveMonthlyPlan?: boolean;
  hasMonthlyPlans?: boolean;
}

export interface IInitialCoursesState {
  status: 'idle' | 'loading' | 'rejected';
  nonEnrolmentStatus: 'idle' | 'loading' | 'rejected';
  redirectUrl?: string;
  // Admin
  adminCoursesData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  adminCourse?: ICourse;
  courseListDataForAdminForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  // User
  userEnrolmentCoursesData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  userEnrolmentCoursesDataForApp?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  userEnrolmentCoursesDataForBundlePayment?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  userNonEnrolmentCoursesData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
  userCourse?: ICourse;
  userEnrolmentCoursesForNotificationData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourse>;
  };
}

const initialState: IInitialCoursesState = {
  status: 'idle',
  nonEnrolmentStatus: 'idle',
  redirectUrl: undefined,
  // Admin
  adminCoursesData: undefined,
  adminCourse: undefined,
  courseListDataForAdminForDropDown: undefined,
  // User
  userEnrolmentCoursesData: undefined,
  userNonEnrolmentCoursesData: undefined,
  userCourse: undefined,
  userEnrolmentCoursesForNotificationData: undefined,
  userEnrolmentCoursesDataForApp: undefined,
  userEnrolmentCoursesDataForBundlePayment: undefined,
};

export const CoursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    resetUserEnrolmentCoursesData: (state) => {
      state.userEnrolmentCoursesData = undefined;
    },
    resetUserEnrolmentCoursesDataForBundlePayment: (state) => {
      state.userEnrolmentCoursesDataForBundlePayment = undefined;
    },
    resetUserNonEnrolmentCoursesData: (state) => {
      state.userNonEnrolmentCoursesData = undefined;
    },
    resetAdminCoursesData: (state) => {
      state.adminCoursesData = undefined;
    },
    resetUserCourse: (state) => {
      state.userCourse = undefined;
    },
    resetAdminCourse: (state) => {
      state.adminCourse = undefined;
    },
    resetRedirectUrl: (state) => {
      state.redirectUrl = undefined;
    },
    resetCourseListDataForAdminForDropDown: (state) => {
      state.courseListDataForAdminForDropDown = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Enrolment courses
      .addCase(getEnrolmentCoursesForUsers.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newUserEnrolmentCoursesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userEnrolmentCoursesData = newUserEnrolmentCoursesData;
      })
      .addCase(getEnrolmentCoursesForUsersForApp.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newUserEnrolmentCoursesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userEnrolmentCoursesDataForApp = newUserEnrolmentCoursesData;
      })
      .addCase(
        getEnrolmentCoursesForUsersForBundlePayment.fulfilled,
        (state, action) => {
          state.status = 'idle';

          const result = action.payload;
          const newUserEnrolmentCoursesData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.userEnrolmentCoursesDataForBundlePayment = newUserEnrolmentCoursesData;
        }
      )
      .addCase(
        getEnrolmentCoursesForUsersForNotification.fulfilled,
        (state, action) => {
          state.status = 'idle';

          const result = action.payload;
          const newUserEnrolmentCoursesForNotificationData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.userEnrolmentCoursesForNotificationData = newUserEnrolmentCoursesForNotificationData;
        }
      )
      .addCase(getNonEnrolmentCoursesForUsers.fulfilled, (state, action) => {
        state.nonEnrolmentStatus = 'idle';

        const result = action.payload;
        const newUserNonEnrolmentCoursesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userNonEnrolmentCoursesData = newUserNonEnrolmentCoursesData;
      })
      // Load more enrolment courses
      .addCase(loadMoreEnrolmentCoursesForUsers.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          state.userEnrolmentCoursesData &&
          state.userEnrolmentCoursesData.list &&
          state.userEnrolmentCoursesData.list.length &&
          action.payload &&
          action.payload.docs &&
          action.payload.docs.length
        ) {
          const newUserEnrolmentCoursesData = {
            ...state.userEnrolmentCoursesData,
            list: [
              ...state.userEnrolmentCoursesData.list,
              ...action.payload.docs,
            ],
          };

          state.userEnrolmentCoursesData = newUserEnrolmentCoursesData;
        }
      })
      // Load more non enrolment courses
      .addCase(
        loadMoreNonEnrolmentCoursesForUsers.fulfilled,
        (state, action) => {
          state.nonEnrolmentStatus = 'idle';

          if (
            state.userNonEnrolmentCoursesData &&
            state.userNonEnrolmentCoursesData.list &&
            state.userNonEnrolmentCoursesData.list.length &&
            action.payload &&
            action.payload.docs &&
            action.payload.docs.length
          ) {
            const newUserNonEnrolmentCoursesData = {
              ...state.userNonEnrolmentCoursesData,
              list: [
                ...state.userNonEnrolmentCoursesData.list,
                ...action.payload.docs,
              ],
            };

            state.userNonEnrolmentCoursesData = newUserNonEnrolmentCoursesData;
          }
        }
      )
      // Get course by id
      .addCase(getCourseByIdForUsers.fulfilled, (state, action) => {
        state.status = 'idle';
        state.userCourse = action.payload;
      })
      // Update course
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.status = 'idle';

        if (state.userCourse && action.payload) {
          state.userCourse = {
            ...state.userCourse,
            ...action.payload,
          };
        } else {
          state.userCourse = action.payload;
        }
      })
      .addCase(updateCourseStudent.fulfilled, (state, action) => {
        if (state.userCourse) {
          const result = action.payload;
          const newCourseData = { ...state.userCourse };

          const courseStudents = [...(state.userCourse.students || [])];

          const index = courseStudents.findIndex(
            (item) => action && result && item._id === result._id
          );

          if (index > -1 && courseStudents[index]) {
            courseStudents[index] = {
              ...courseStudents[index],
              autoTopupEnabled: result.autoTopupEnabled,
              autoTopupHours: result.autoTopupHours,
            };

            newCourseData.students = [...courseStudents];
          }

          state.userCourse = {
            ...newCourseData,
          };
        }
      })
      // Admin
      // Admin get courses
      .addCase(getCoursesForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newAdminCoursesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCoursesData = newAdminCoursesData;
      })
      // Admin loadmore courses
      .addCase(loadMoreCoursesForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          state.adminCoursesData &&
          state.adminCoursesData.list &&
          state.adminCoursesData.list.length &&
          action.payload &&
          action.payload.docs &&
          action.payload.docs.length
        ) {
          const newAdminCoursesData = {
            ...state.adminCoursesData,
            list: [...state.adminCoursesData.list, ...action.payload.docs],
          };

          state.adminCoursesData = newAdminCoursesData;
        }
      })
      // Admin get course by id
      .addCase(getCourseByIdForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminCourse = action.payload;
      })
      // Admin create course
      .addCase(createCourseAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminCourse = action.payload;
      })
      .addCase(createCourseByTemplateAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.payload) {
          state.redirectUrl = `${process.env.PUBLIC_URL}/portal/courses/${action.payload._id}`;
        }
      })
      // Admin update course
      .addCase(updateCourseAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.adminCourse = action.payload;
      })
      // Admin update course for saving slice
      .addCase(updateCourseAsAdminSavingSlice.fulfilled, (state, action) => {
        state.adminCourse = action.payload;
      })
      // Admin Delete Course
      .addCase(deleteCourseAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload._id &&
          state.adminCourse &&
          state.adminCourse._id &&
          state.adminCourse._id === action.payload._id
        ) {
          const isClass = state.adminCourse.type === COURSE_TYPES.CLASS;
          state.redirectUrl = `${process.env.PUBLIC_URL}/portal/${
            isClass ? 'classes' : 'courses'
          }`;
        }
      })
      // Create course tutor
      .addCase(createCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminCourse &&
          result &&
          result.course &&
          result.course._id === state.adminCourse._id
        ) {
          const newAdminCourse = {
            ...state.adminCourse,
            tutors: [...(state.adminCourse.tutors || []), result],
          };

          if (result.groupClassId) {
            newAdminCourse.groupClassId = result.groupClassId;
          }

          state.adminCourse = newAdminCourse;
        }
      })
      // Delete course tutor
      .addCase(deleteCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminCourse &&
          state.adminCourse.tutors &&
          state.adminCourse.tutors.length &&
          result &&
          result.course &&
          result.course._id === state.adminCourse._id
        ) {
          const newAdminCourse = {
            ...state.adminCourse,
            tutors: state.adminCourse.tutors.filter(
              (item) => item._id !== result._id
            ),
          };

          state.adminCourse = newAdminCourse;
        }
      })
      // Update course tutor
      .addCase(updateCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminCourse &&
          state.adminCourse.tutors &&
          state.adminCourse.tutors.length &&
          result &&
          result.course &&
          result.course._id === state.adminCourse._id
        ) {
          const newAdminCourse = {
            ...state.adminCourse,
          };

          if (result.status === 'ENABLE') {
            const newTutors = newAdminCourse.tutors || [];

            const index = newTutors.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newTutors[index] = result;
            } else {
              newTutors.push(result);
            }

            newAdminCourse.tutors = newTutors;
          } else {
            newAdminCourse.tutors = state.adminCourse.tutors.filter(
              (item) => item._id !== result._id
            );
          }

          state.adminCourse = newAdminCourse;
        }
      })
      .addCase(getCourseListForDropDownAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;

        const newCourseListDataForAdminForDropDown = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.courseListDataForAdminForDropDown = newCourseListDataForAdminForDropDown;
      })
      // Update course student learning details
      .addCase(
        updateCourseStudentLearningDetails.fulfilled,
        (state, action) => {
          state.status = 'idle';

          if (state.userCourse && action.payload) {
            const newUserCourse = {
              ...state.userCourse,
            };

            if (
              newUserCourse.students &&
              newUserCourse.students.length &&
              action.payload
            ) {
              const index = newUserCourse.students.findIndex(
                (student) => student._id === action.payload._id
              );

              if (index > -1) {
                newUserCourse.students[index] = {
                  ...newUserCourse.students[index],
                  learningDetails: action.payload.learningDetails,
                };
              }
            }

            state.userCourse = newUserCourse;
          }
        }
      );
    // Matcher
    builder
      .addMatcher(
        isPending(
          getEnrolmentCoursesForUsers,
          loadMoreEnrolmentCoursesForUsers,
          getCourseByIdForUsers,
          getCoursesForAdmin,
          loadMoreCoursesForAdmin,
          getCourseByIdForAdmin,
          createCourseAsAdmin,
          updateCourseAsAdmin,
          deleteCourseAsAdmin,
          createCourseByTemplateAsAdmin,
          getCourseListForDropDownAsAdmin,
          updateCourse,
          sendCourseCreditReminderNotification
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isPending(
          getNonEnrolmentCoursesForUsers,
          loadMoreNonEnrolmentCoursesForUsers,
          getEnrolmentCoursesForUsersForBundlePayment
        ),
        (state) => {
          state.nonEnrolmentStatus = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getEnrolmentCoursesForUsers,
          loadMoreEnrolmentCoursesForUsers,

          getCourseByIdForUsers,
          getCoursesForAdmin,
          loadMoreCoursesForAdmin,
          getCourseByIdForAdmin,
          createCourseAsAdmin,
          updateCourseAsAdmin,
          deleteCourseAsAdmin,
          createCourseByTemplateAsAdmin,
          getCourseListForDropDownAsAdmin,
          updateCourse,
          sendCourseCreditReminderNotification
        ),
        (state) => {
          state.status = 'rejected';
        }
      )
      .addMatcher(
        isRejected(
          getNonEnrolmentCoursesForUsers,
          getEnrolmentCoursesForUsersForBundlePayment,
          loadMoreNonEnrolmentCoursesForUsers
        ),
        (state) => {
          state.nonEnrolmentStatus = 'rejected';
        }
      );
  },
});

export const {
  resetUserEnrolmentCoursesData,
  resetUserNonEnrolmentCoursesData,
  resetAdminCoursesData,
  resetUserCourse,
  resetAdminCourse,
  resetRedirectUrl,
  resetCourseListDataForAdminForDropDown,
  resetUserEnrolmentCoursesDataForBundlePayment,
} = CoursesSlice.actions;

export const getCoursesState = (state: RootState): RootState['courses'] =>
  state.courses;

export * from './api';
export * from './adminApi';
export default CoursesSlice.reducer;
